// Children tab: one row per child (name, date of birth, who the child lives with, timeshare with the other parent),
// with the Santa Clara standard-arrangement table and the timeshare worksheet (reused from the hybrid app).
import { SANTA_CLARA_TABLE, type Case, type Child, type PartyIndex } from '@divorceos/schema';
import { useCaseView } from '../caseView';
import { ageAt } from '../model';
import { useDis } from '../store';
import { ReleasePicker } from '../ReleasePicker';
import { syncPartiesFromChildren, timeshareWith } from '../../lib/children';
import { childNcpPercent, timeshareFromDays, timeshareFromPercent } from '../../lib/timeshare';
import { fmtInput } from '../../lib/format';
import { isAttached } from '../../timeshare/worksheet';

const MAX_CHILDREN = 10;

export function Children() {
  const { c, labels } = useCaseView();
  const update = useDis((s) => s.update);
  const setUi = useDis((s) => s.setUi);
  const toast = useDis((s) => s.toast);
  const edit = (fn: (x: Case) => void) => update((x) => { fn(x); syncPartiesFromChildren(x); });
  const setChild = (i: number, fn: (ch: Child) => void) => edit((x) => { const ch = x.children[i]; if (ch) fn(ch); });
  const kids = c.children;
  const order = kids.map((k, i) => ({ k, i })).sort((a, b) => String(a.k.dateOfBirth ?? '9999').localeCompare(String(b.k.dateOfBirth ?? '9999')));

  return (
    <>
      <div className="sec">
        <h4>Children of this relationship</h4>
        <table className="tbl">
          <thead><tr><th>Name</th><th>Date of birth</th><th className="n">Age</th><th>Lives with</th><th>Arrangement (Santa Clara table)</th><th className="n">% with other parent</th><th>Worksheet</th><th /></tr></thead>
          <tbody>
            {order.map(({ k: ch, i }) => {
              const ws = isAttached(ch);
              return (
                <tr key={i}>
                  <td><input aria-label="Child name" value={ch.name} onChange={(e) => setChild(i, (x) => { x.name = e.target.value; })} /></td>
                  <td><input type="date" aria-label="Date of birth" value={ch.dateOfBirth ?? ''} onChange={(e) => setChild(i, (x) => { x.dateOfBirth = e.target.value || undefined; })} /></td>
                  <td className="n">{ageAt(ch.dateOfBirth, c.taxYear)}</td>
                  <td>
                    <select aria-label="Lives with" value={ch.custodialParent} onChange={(e) => setChild(i, (x) => { x.custodialParent = Number(e.target.value) as PartyIndex; })}>
                      <option value={0}>{labels[0]}</option><option value={1}>{labels[1]}</option>
                    </select>
                  </td>
                  <td>
                    <select aria-label="Arrangement" value={ch.timeshareSource === 'TABLE' ? ch.tableKey ?? 'x' : 'x'} disabled={ws}
                      onChange={(e) => setChild(i, (x) => {
                        const row = SANTA_CLARA_TABLE.find((r) => r.key === e.target.value);
                        if (row) { x.timeshare = timeshareFromDays(row.days); x.tableKey = row.key; x.timeshareSource = 'TABLE'; } else { x.tableKey = 'x'; x.timeshareSource = 'DIRECT'; }
                      })}>
                      <option value="x">{ws ? 'From worksheet' : 'Custom'}</option>
                      {SANTA_CLARA_TABLE.map((r) => <option key={r.key} value={r.key}>{r.key} · {r.description} ({Math.round((r.days / 365) * 1000) / 10}%)</option>)}
                    </select>
                  </td>
                  <td className="n">
                    <input aria-label="Timeshare percent" inputMode="decimal" style={{ width: 64, textAlign: 'right' }} disabled={ws}
                      value={fmtInput(childNcpPercent(ch), 2)}
                      onChange={(e) => { const n = Math.max(0, Math.min(100, Number(e.target.value.replace(/[%\s]/g, '')) || 0)); setChild(i, (x) => { x.timeshare = timeshareFromPercent(n); x.tableKey = 'x'; x.timeshareSource = 'DIRECT'; }); }}
                      onBlur={() => { if (Math.abs(childNcpPercent(ch) - 50) < 1e-9) { setChild(i, (x) => { x.timeshare = timeshareFromPercent(49.99); x.tableKey = 'x'; x.timeshareSource = 'DIRECT'; }); toast(`Set to 49.99% so ${labels[ch.custodialParent]} (the parent the child lives with) can file head of household.`); } }} />
                  </td>
                  <td><button type="button" className="lnkbtn" onClick={() => setUi({ dialog: 'worksheet', dialogArg: i })}>{ws ? 'Edit worksheet' : 'Open'}</button></td>
                  <td><button type="button" className="lnkbtn" aria-label="Remove child" onClick={() => edit((x) => { x.children.splice(i, 1); })}>Remove</button></td>
                </tr>
              );
            })}
            {kids.length === 0 && <tr><td colSpan={8} className="muted">No children entered. Spousal support only.</td></tr>}
          </tbody>
        </table>
        <button type="button" className="btn sm" disabled={kids.length >= MAX_CHILDREN} onClick={() => edit((x) => {
          const cust: PartyIndex = x.children[0]?.custodialParent ?? 1;
          x.children.push({ name: `Child ${x.children.length + 1}`, dateOfBirth: `${c.taxYear - 6}-01-01`, custodialParent: cust, timeshare: timeshareFromPercent(20), tableKey: 'x', timeshareSource: 'DIRECT' });
        })}>+ Add child</button>
        <p className="muted" style={{ margin: '10px 0 0', fontSize: 11.5 }}>
          Children are listed oldest first. Timeshare is the share of time with the parent the child does not live with. Average time with {labels[0]}: <b>{fmtInput(timeshareWith(kids, 0), 2)}%</b>; with {labels[1]}: <b>{fmtInput(timeshareWith(kids, 1), 2)}%</b>.
          {kids.some((k) => Math.abs(childNcpPercent(k) - 50) < 1e-9) ? ' A child at exactly 50% is treated as equal custody: dependents are claimed under the tie-breaker rules.' : ''}
        </p>
      </div>
      <div className="sec">
        <h4>Dependency releases (Form 8332)</h4>
        <p className="muted" style={{ margin: '0 0 8px' }}>
          Releases move dependents from the parent with the lower net income to the one with the higher net income. Pick the children here or on the Support tab; with no selection the Proposed panel releases every available dependent. Whether releases are allowed is set on the Support tab; the Exemption releases report compares the combinations.
        </p>
        <ReleasePicker compact />
      </div>
      <div className="sec">
        <h4>Children of other relationships</h4>
        <p className="muted" style={{ margin: '0 0 8px' }}>Enter them on the Hardship deduction row of Case Data (Other children in the home). disOS computes the FC 4071 hardship amount. Their child care goes on the Child support add-ons row.</p>
      </div>
    </>
  );
}
